import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import classes from './team.module.scss';
import FilterButton from '../../_common/FilterButton/FilterButton';
import InputField from '../../_common/Form/TextFields/InputField';
import Button from '../../_common/Form/Buttons/Button';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import accountHelpers from '../../../utils/accountHelpers';
import { useQuery } from '../../../utils/hooks/useQuery';
import utils from '../../../utils/utils';
import { getTeamRequest } from '../../../store/actions/team';
import TeamTable from './TeamTable/TeamTable';
import Loader from '../../_common/Loader/Loader';
import { changeMenuPath } from '../../../store/actions/menu';

const specialDataAdmin = [
  {
    title: 'Admin',
    key: 'userRole',
    value: 'Admin',
  },
];

const specialDataPharmacy = [
  {
    title: 'Pharmacy',
    key: 'userRole',
    value: 'Pharmacy',
  },
];

const specialDataSales = [
  {
    title: 'Sales',
    key: 'userRole',
    value: 'Rep',
  },
];

const specialDataHealth = [
  {
    title: 'Health',
    key: 'userRole',
    value: 'Health',
  },
];

const specialDataPhysicians = [
  {
    title: 'Physicians',
    key: 'userRole',
    value: 'Physician',
  },
];

const specialDataPatients = [
  {
    title: 'Patients',
    key: 'userRole',
    value: 'Patient',
  },
];

const specialDataManufacturerUsers = [
  {
    title: 'Manufacturer Users',
    key: 'userRole',
    value: 'Manufacturer Users',
  },
];

const specialDataManufacturer = [
  {
    title: 'Manufacturer',
    key: 'userRole',
    value: 'Manufacturer',
  },
];

const inputData = [
  {
    name: 'Name',
    path: 'name',
  },
  {
    name: 'Username',
    path: 'username',
  },
  {
    name: 'Email',
    path: 'email',
  },
];

const activeData = [
  {
    title: 'Inactive',
    key: 'active',
    value: '1',
  },
];

const activationRequestData = [
  {
    title: 'Activation Request',
    key: 'activationRequest',
    value: '1',
  },
];

let timeout;

function Team() {
  const isAdmin = accountHelpers.isInRole('Admin');
  const isManufacturerUsers = accountHelpers.isInOneOfRoles(['Manufacturer Users']);
  const hideFiltersByRole = accountHelpers.isInOneOfRoles(['Rep', 'Manufacturer Users']);

  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { query, setQuery, queryToStringify } = useQuery();
  const { teamData, teamTotalPage } = useSelector((store) => store.team);
  const navigate = useNavigate();

  const [requestData, setRequestData] = useState({
    searcherId: accountHelpers.getUser().id,
    page: query.page || 1,
    limit: query.limit || 15,
    name: query.name || '',
    username: query.username || '',
    email: query.email || '',
    userRole: query.userRole ? query.userRole.split(',') : [],
  });
  const [loading, setLoading] = useState(true);
  const specialData = [
    specialDataAdmin, specialDataPharmacy, specialDataSales, specialDataPhysicians,
    specialDataHealth, specialDataPatients, specialDataManufacturerUsers, specialDataManufacturer,
  ];
  const { limit, page } = requestData;

  useEffect(() => {
    const newQuery = utils.deleteEmptyKeys({ ...requestData });

    delete newQuery.searcherId;

    if (isManufacturerUsers) {
      newQuery.userRole = 'Manufacturer';
    } else if (!_.isEmpty(newQuery.userRole)) {
      newQuery.userRole = newQuery.userRole.join(',');
    } else {
      delete newQuery.userRole;
    }
    setQuery(newQuery);
  }, [requestData]);

  useEffect(() => {
    setLoading(true);

    const newQuery = utils.deleteEmptyKeys(requestData);

    if (isManufacturerUsers) {
      newQuery.userRole = 'Manufacturer';
    } else if (!_.isEmpty(newQuery.userRole)) {
      newQuery.userRole = newQuery.userRole.join(',');
    } else {
      delete newQuery.userRole;
    }

    (async () => {
      await dispatch(getTeamRequest(newQuery));

      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    dispatch(changeMenuPath('My Team', `${pathname}${queryToStringify(query)}`));
  }, [query]);

  const filterButtonClick = async ([{ value }]) => {
    setLoading(true);
    const userRole = requestData.userRole.includes(value)
      ? requestData.userRole.filter((d) => d !== value)

      : [...requestData.userRole, value];

    setRequestData((prev) => ({
      ...prev,
      page: 1,
      userRole,
    }));

    const newQuery = utils.deleteEmptyKeys({ ...requestData, userRole });

    if (!_.isEmpty(newQuery.userRole)) {
      newQuery.userRole = newQuery.userRole.join(',');
    } else {
      delete newQuery.userRole;
    }

    await dispatch(getTeamRequest(newQuery));

    setLoading(false);
  };

  const searchFilterChange = (key, value) => {
    setLoading(true);
    const activateTabs = ['active', 'activationRequest'];

    setRequestData((prev) => {
      const newData = { ...prev, [key]: value };

      if (activateTabs.includes(key) && prev[key]) {
        delete newData[key];
      }

      return newData;
    });

    clearTimeout(timeout);

    const newQuery = utils.deleteEmptyKeys({
      ...requestData,
      [key]: activateTabs.includes(key) && requestData[key] ? '' : value,
    });

    if (isManufacturerUsers) {
      newQuery.userRole = 'Manufacturer';
    } else if (!_.isEmpty(newQuery.userRole)) {
      newQuery.userRole = newQuery.userRole.join(',');
    } else {
      delete newQuery.userRole;
    }

    timeout = setTimeout(async () => {
      await dispatch(getTeamRequest(newQuery));

      setLoading(false);
    }, 400);
  };

  const clearFilters = async () => {
    setLoading(true);

    const clearData = {
      page: 1,
      limit: 15,
      name: '',
      username: '',
      email: '',
      userRole: [],
    };

    setRequestData(clearData);

    const newQuery = utils.deleteEmptyKeys(clearData);

    if (isManufacturerUsers) {
      newQuery.userRole = 'Manufacturer';
    } else if (!_.isEmpty(newQuery.userRole)) {
      newQuery.userRole = newQuery.userRole.join(',');
    } else {
      delete newQuery.userRole;
    }

    await dispatch(getTeamRequest(newQuery));

    setLoading(false);
  };

  const onChangePageData = async (path, count) => {
    const newQuery = utils.deleteEmptyKeys({ ...requestData, [path]: count });

    if (path === 'limit') {
      newQuery.page = 1;
    }

    setRequestData({ ...newQuery });

    if (!_.isEmpty(newQuery.userRole)) {
      newQuery.userRole = newQuery.userRole.join(',');
    } else delete newQuery.userRole;

    await dispatch(getTeamRequest(newQuery));
  };

  const filters = useMemo(() => {
    const isPharmacy = !!(accountHelpers.isInRole('Pharmacy') && accountHelpers.getUser().Manufacturer?.length
      && !accountHelpers.isLoggedInAsPriorityPharmacies());

    if (isPharmacy) {
      return specialData.filter((d) => ['Manufacturer Users', 'Manufacturer'].includes(d[0].title));
    }

    return specialData;
  }, []);

  return (
    <>
      <div className={classes.filter_wrapper}>
        <div className={classes.filter_button_wrapper}>
          {!hideFiltersByRole && filters.map((s) => (
            <div className={classes.filter_button} key={s[0].title}>
              <FilterButton
                onClick={() => filterButtonClick(s)}
                activeValue={requestData.userRole.includes(s[0].value) ? s[0].value : ''}
                buttonsData={s}
              />
            </div>
          ))}
        </div>

        <div className={classes.filter_second_wrapper}>
          <div className={classes.filter_input_wrapper}>
            {inputData.map((i) => (
              <div key={i.name} className={classes.filter_input}>
                <InputField
                  label={i.name}
                  value={requestData[i.path]}
                  onChange={(value) => searchFilterChange(i.path, value)}
                />
              </div>
            ))}

            {isAdmin && (
              <>
                <div className={classes.active_button}>
                  <FilterButton
                    onClick={searchFilterChange}
                    activeValue={requestData.active}
                    buttonsData={activeData}
                  />
                </div>

                <div className={classes.activation_button}>
                  <FilterButton
                    onClick={searchFilterChange}
                    activeValue={requestData.activationRequest}
                    buttonsData={activationRequestData}
                  />
                </div>
              </>
            )}

            <div className={classes.clear_button_wrapper}>
              <Button
                clearButton
                title="Clear"
                onClick={clearFilters}
              />
            </div>
          </div>

          <div className={classes.buttons}>
            <Button
              className={classes.add_button}
              title={(
                <div className={classes.add_button_text}>
                  <PlusIcon className={classes.add_button_svg} />
                  Add new user
                </div>
                )}
              onClick={() => navigate('/team/add')}
            />
          </div>
        </div>
      </div>

      {loading
        ? <div className={classes.loader_wrapper}><Loader /></div>

        : (
          <div className={classes.table_wrapper}>
            <TeamTable
              data={teamData}
              onChange={onChangePageData}
              totalPage={+teamTotalPage}
              limit={+limit}
              page={+page - 1}
              className={classes.table}
            />
          </div>
        )}
    </>
  );
}

export default Team;
